import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Divider,
    Alert,
    CircularProgress,
    Tooltip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from '@mui/material';
import {
    DescriptionOutlined as LeaseIcon,
    Visibility as PreviewIcon,
    CloseRounded as CloseIcon
} from '@mui/icons-material';
import {
    Button as RButton,
    Card as RCard,
    CardHeader as RCardHeader,
    Collapse as RCollapse,
} from 'reactstrap';
import FeatherIcon from "feather-icons-react";
import { getLeasesByProperty } from './api';
import { PreviewExistingLease } from './ExistingLeasePreview/PreviewExistingLease';
import './leases.scss';
import { LeasesGrid } from './LeaseCard';

const Leases = ({ propertyId, buildingName, unitNo }) => {
    const [leases, setLeases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedLeaseId, setSelectedLeaseId] = useState(null);

    useEffect(() => {
        const fetchLeases = async () => {
            setLoading(true);
            setError(null);
            try {
                // Check if required params are available
                if (!propertyId || !buildingName || !unitNo) {
                    console.warn('Missing required parameters for lease fetch', { propertyId, buildingName, unitNo });
                    setLeases([]);
                    setLoading(false);
                    return;
                }

                const response = await getLeasesByProperty(propertyId, buildingName, unitNo);

                if (!response || !response.success) {
                    throw new Error(response?.message || 'Failed to fetch leases');
                }

                setLeases(response.data || []);
            } catch (err) {
                setLeases([]);
            } finally {
                setLoading(false);
            }
        };

        fetchLeases();
    }, [propertyId, buildingName, unitNo]);

    useEffect(() => {
        if (previewOpen) {
            window.history.pushState({ modalOpen: true }, '');

            const handlePopState = (event) => {
                if (previewOpen) {
                    setPreviewOpen(false);
                    window.history.pushState({ modalOpen: false }, '');
                }
            };

            window.addEventListener('popstate', handlePopState);

            return () => {
                window.removeEventListener('popstate', handlePopState);
            };
        }
    }, [previewOpen]);

    const handlePreviewOpen = (leaseId) => {
        setSelectedLeaseId(leaseId);
        setPreviewOpen(true);
    };

    const handlePreviewClose = () => {
        setPreviewOpen(false);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress />
                <Typography variant="body1" ml={2}>Loading leases...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Alert severity="warning" sx={{ my: 2 }}>Unable to load leases information. Try again later.</Alert>;
    }

    return (
        <Box>
            {leases.length === 0 ? (
                <Alert severity="info" sx={{ my: 2 }}>
                    There is currently no lease associated with this unit number.
                </Alert>
            ) : (
                <LeasesGrid leases={leases} handlePreviewOpen={handlePreviewOpen} />
            )}

            <Dialog
                open={previewOpen}
                onClose={handlePreviewClose}
                maxWidth="md"
                fullWidth
                fullScreen
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Tooltip title="Close">
                        <IconButton onClick={handlePreviewClose} size="small" sx={{ color: 'red' }}>
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ height: '100vh' }}>
                        <PreviewExistingLease leaseId={selectedLeaseId} handleClose={handlePreviewClose} />
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

const LeasesWrapper = ({ propertyId, buildingName, unitNo }) => {
    const [isOpen, setIsOpen] = useState(false);

    const hasRequiredProps = Boolean(propertyId && buildingName && unitNo);
    const toggleCollapse = () => setIsOpen((prev) => !prev);

    if (!hasRequiredProps) {
        return (
            <RCard className="mb-4">
                <RCardHeader className="d-flex justify-content-between align-items-center">
                    <div className="p-0">
                        Leases History
                        <Typography variant="caption" component="div">
                            Complete the form with property, building and unit information to view leases.
                        </Typography>
                    </div>
                </RCardHeader>
            </RCard>
        );
    }

    return (
        <RCard className="mb-4">
            <RCardHeader className="py-3 text-center">
                <RButton
                    type="button"
                    color="primary"
                    outline
                    className="fw-bold px-4 py-2 rounded-pill"
                    onClick={toggleCollapse}
                >
                    <FeatherIcon icon="clock" className="me-2" size={16} />
                    Lease History
                </RButton>
            </RCardHeader>
            <RCollapse isOpen={isOpen}>
                <CardContent>
                    <Leases propertyId={propertyId} buildingName={buildingName} unitNo={unitNo} />
                </CardContent>
            </RCollapse>
        </RCard>
    );
};

export { LeasesWrapper as Leases };